import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { canUpdateCategoryShare } from '../../utils/categoryShares';
import { API_BASE } from '../../config';
import DataSection from './Form5/DataSection';
import OchagSection from './Form5/OchagSection';
import OchagCardsSection from './Form5/OchagCardsSection';
import CalculationsSection from './Form5/CalculationsSection';
import InfoSection from './Form5/InfoSection';
import CauseSignModal from './Form5/CauseSignModal';
import PickerModal from './Form5/PickerModal';
import YearPickerModal from './Form5/YearPickerModal';
import CauseModal from './Form5/CauseModal';
import { getEmptyFormData } from './Form5/constants';
import { calculateDamage_0, isDamageSumValid, calculateDamagePercentage } from './Form5/utils';
import { usePloshadVydel, useTotalDensitySum, useYarusTotals, useTotalsByNasazhdeniya, useDamageCalculations } from './Form5/calculationHooks';
import { usePicker } from './Form5/pickerHooks';

const Form5 = ({ handbooks, formData, setFormData, selectedDoc, form2Data, independentForm2Data, dataType, form1Data, isActive, hasValidationErrors, setHasValidationErrors }) => {

  // Ref для отслеживания инициализации
  const isInitializedRef = useRef(false);
  const prevForm2LengthRef = useRef(0);
  const prevIsActiveRef = useRef(false);

  // Состояние для скроллбара таблицы
  const [tableScrollPosition, setTableScrollPosition] = useState(0);
  const tableScrollRef = useRef(null);

  // Состояние для скроллбара таблицы расчетов
  const [calculationsScrollPosition, setCalculationsScrollPosition] = useState(0);
  const calculationsScrollRef = useRef(null);

  // Состояние для модального окна причины/признака
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [modalData, setModalData] = useState([]);

  // Состояние для picker года
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [activeYearItemId, setActiveYearItemId] = useState(null);

  // Состояние для загрузки признаков
  const [isLoadingSigns, setIsLoadingSigns] = useState(false);
  const [loadedSigns, setLoadedSigns] = useState({});
  const [previousCause, setPreviousCause] = useState({});

  // Состояние для модального окна причин
  const [showCauseModal, setShowCauseModal] = useState(false);
  const [selectedRowIdForCause, setSelectedRowIdForCause] = useState(null);

  // Состояние для выбранных очагов (максимум можно отметить MAX_SELECTED_OCHAGS)
  const MAX_SELECTED_OCHAGS = 10;
  const [selectedOchags, setSelectedOchags] = useState(new Set());

  // Состояние для данных очагов (главный/не главный, ВО, фаза, тип)
  const [ochagData, setOchagData] = useState({});

  // Состояние для модального окна редактирования очага
  const [isOchagModalOpen, setIsOchagModalOpen] = useState(false);
  const [editingOchagKey, setEditingOchagKey] = useState(null);


  // Сбрасываем флаг инициализации при активации формы и загружаем данные очагов (точно как в Form4)
  useEffect(() => {
    if (isActive && !prevIsActiveRef.current) {
      // Сбрасываем флаг инициализации
      isInitializedRef.current = false;
      
      // Загружаем данные очагов из formData при активации формы
      // ВАЖНО: устанавливаем флаг только если есть реальные данные
      const hasSelectedOchags = formData?.selectedOchags && Array.isArray(formData.selectedOchags) && formData.selectedOchags.length > 0;
      const hasOchagData = formData?.ochagData && typeof formData.ochagData === 'object' && Object.keys(formData.ochagData).length > 0;
      
      if (formData?.selectedOchags !== undefined) {
        const ochagsArray = Array.isArray(formData.selectedOchags) ? formData.selectedOchags : [];
        setSelectedOchags(new Set(ochagsArray));
      }
      
      if (formData?.ochagData !== undefined) {
        const ochagDataObj = formData.ochagData && typeof formData.ochagData === 'object' 
          ? formData.ochagData 
          : {};
        setOchagData(ochagDataObj);
      }
      
      // Устанавливаем флаг только если есть реальные данные
      if (hasSelectedOchags || hasOchagData) {
        ochagDataLoadedRef.current = true;
      } else {
        // Если данных нет, сбрасываем флаг, чтобы загрузить из form6Data
        ochagDataLoadedRef.current = false;
      }
    }
    
    // Сбрасываем флаг загрузки при деактивации формы
    if (!isActive) {
      ochagDataLoadedRef.current = false;
    }
    
    prevIsActiveRef.current = isActive;
  }, [isActive, formData]);

  // Функция для обновления данных формы
  const updateFormData = useCallback((newData) => {
    setFormData(newData);
  }, [setFormData]);

  // Используем функции из utils
  const calculateDamage_0_func = useCallback((item) => calculateDamage_0(item), []);
  const isDamageSumValid_func = useCallback((item) => isDamageSumValid(item), []);
  const calculateDamagePercentage_func = useCallback((item) => calculateDamagePercentage(item), []);


  // Функция для обновления элемента данных
  const updateDataItem = useCallback((id, field, value) => {
    const currentFormData = formData || getEmptyFormData();
    const newData = currentFormData.data.map(item => {
      if (item.id === id) {
        const categoryFields = ['objedola25', 'objedola50', 'objedola75', 'objedola100'];
        if (categoryFields.includes(field) && !canUpdateCategoryShare(item, field, value, categoryFields)) return item;
        let processedValue;
        
        if (value === '' || value === undefined || value === null) {
          processedValue = '';
        } else {
          // Удаляем ведущие нули для числовых полей
          const numericFields = ['objedola25', 'objedola50', 'objedola75', 'objedola100'];
          if (numericFields.includes(field)) {
            processedValue = value.replace(/^0+/, '') || '0';
            if (processedValue === '') processedValue = '';
          } else {
            processedValue = value;
          }
        }
        
        return { ...item, [field]: processedValue };
      }
      return item;
    });
    
    updateFormData({ ...currentFormData, data: newData });
  }, [formData, updateFormData]);

  // Получаем данные для отображения
  const displayData = useMemo(() => {
    if (formData) {
      return formData;
    }
    const empty = getEmptyFormData();
    return empty;
  }, [formData]);

  // Получаем сохраненные данные модального окна из formData
  const savedModalData = useMemo(() => {
    return displayData.savedModalData || {};
  }, [displayData.savedModalData]);

  // Используем хуки для расчетов (аналогично Form4)
  const { calculatePloshadVydel } = usePloshadVydel(form1Data, dataType);
  const { calculateTotalDensitySum } = useTotalDensitySum(form1Data, independentForm2Data, calculatePloshadVydel);
  const { calculateYarusTotals } = useYarusTotals(displayData, calculateDamage_0_func, calculateDamagePercentage_func);
  const { calculateTotalsByNasazhdeniya } = useTotalsByNasazhdeniya(
    displayData,
    independentForm2Data,
    form1Data,
    calculatePloshadVydel,
    calculateTotalDensitySum,
    calculateYarusTotals,
    calculateDamage_0_func,
    calculateDamagePercentage_func
  );

  // Используем хук для расчетов итогов
  const {
    calculateTotalDamage_0,
    calculateTotalDamage_1_25,
    calculateTotalDamage_25_1_50,
    calculateTotalDamage_50_1_75,
    calculateTotalDamage_75_1_100,
    calculateTotalDamagePercentage
  } = useDamageCalculations(displayData, calculateDamage_0_func, calculateDamagePercentage_func, calculateTotalsByNasazhdeniya);


  // Функции для работы с модальным окном
  const openModal = useCallback((rowId) => {
    setSelectedRowId(rowId);
    
    // Загружаем сохраненные данные для этой строки или создаем пустые
    const savedData = savedModalData[rowId] || [{ id: 1, prichina: '', godpovr: '', prizn: '', dolaprizn: '' }];
    setModalData(savedData);
    setIsModalOpen(true);
  }, [savedModalData]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setModalData([]);
    setSelectedRowId(null);
  }, []);

  const addModalRow = () => {
    setModalData(prevModalData => {
      const newId = Math.max(...prevModalData.map(item => item.id), 0) + 1;
      return [...prevModalData, { id: newId, prichina: '', godpovr: '', prizn: '', dolaprizn: '' }];
    });
  };

  const removeModalRow = (id) => {
    setModalData(prevModalData => prevModalData.filter(item => item.id !== id));
  };

  const saveModalData = useCallback(() => {
    if (selectedRowId !== null) {
      const currentFormData = formData || getEmptyFormData();
      const newFormData = {
        ...currentFormData,
        savedModalData: {
          ...(currentFormData?.savedModalData || {}),
          [selectedRowId]: modalData
        }
      };
      updateFormData(newFormData);
    }
    closeModal();
  }, [selectedRowId, modalData, formData, updateFormData, closeModal]);

  const updateModalData = useCallback((itemId, field, value) => {
    setModalData(prevModalData => 
      prevModalData.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  }, []);


  // Функция для работы с информацией пользователя
  const handleUserInfoChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // Сохраняем в formData
    const currentFormData = formData || getEmptyFormData();
    const newFormData = { ...currentFormData, itaks5: newValue };
    updateFormData(newFormData);
  }, [formData, updateFormData]);

  // Функция для загрузки признаков по причине (как в Form4)
  const loadSignsForCause = useCallback(async (causeName) => {
    if (!causeName) return;
    
    // Проверяем, есть ли уже загруженные признаки для этой причины
    if (loadedSigns[causeName]) {
      return loadedSigns[causeName];
    }
    
    setIsLoadingSigns(true);
    try {
      const response = await fetch(`${API_BASE}/get-signs/?name=${encodeURIComponent(causeName)}`);
      if (response.ok) {
        const data = await response.json();
        const signs = data.signs || [];
        const signsOptions = signs.map((sign, index) => {
          const signName = typeof sign === 'object' ? sign.name : sign;
          const signId = typeof sign === 'object' ? sign.id : sign;
          return {
            value: signId.toString(),
            label: signName,
            description: null
          };
        });
        
        // Сохраняем загруженные признаки
        setLoadedSigns(prev => ({
          ...prev,
          [causeName]: signsOptions
        }));
        
        return signsOptions;
      } else {
        return [];
      }
    } catch (error) {
      return [];
    } finally {
      setIsLoadingSigns(false);
    }
  }, [loadedSigns]);

  const closeYearPicker = useCallback(() => {
    setShowYearPicker(false);
    setActiveYearItemId(null);
  }, []);

  const openYearPicker = useCallback((itemId) => {
    setActiveYearItemId(itemId);
    setShowYearPicker(true);
  }, []);


  // Функция-хелпер для сохранения данных очага (по ключу и по коду причины)
  const updateOchagData = useCallback((key, data) => {
    const newOchagData = { ...ochagData };
    
    // Получаем ID причины из ключа (формат: "ID-индекс")
    const causeId = key.split('-')[0];
    
    // Находим причину в справочнике для получения кода
    const causesHandbook = handbooks.find(h => h.id === 10);
    let causeCode = null;
    if (causesHandbook && causesHandbook.references) {
      const cause = causesHandbook.references.find(
        r => r.id.toString() === causeId
      );
      if (cause && cause.code) {
        causeCode = String(cause.code);
      }
    }
    
    // Получаем текущие данные по ключу (если есть)
    const currentDataByKey = newOchagData[key] || {};
    
    // Объединяем текущие данные с новыми
    const updatedData = {
      ...currentDataByKey,
      ...data
    };
    
    // Сохраняем данные по ключу с индексом (для редактирования)
    newOchagData[key] = updatedData;
    
    // Также сохраняем данные по коду причины (для отображения)
    if (causeCode) {
      // Получаем текущие данные по коду (если есть)
      const currentDataByCode = newOchagData[causeCode] || {};
      // Объединяем все данные (и старые по коду, и новые)
      newOchagData[causeCode] = {
        ...currentDataByCode,
        ...updatedData
      };
    }
    
    setOchagData(newOchagData);
  }, [ochagData, handbooks]);

  // Функция-хелпер для получения данных очага по ключу (ищет и по ключу, и по коду причины)
  const getOchagDataByKey = useCallback((key) => {
    // Сначала пробуем найти по ключу
    let data = ochagData[key] || null;
    
    // Если не нашли, пробуем найти по коду причины
    if (!data) {
      const causeId = key.split('-')[0];
      const causesHandbook = handbooks.find(h => h.id === 10);
      if (causesHandbook && causesHandbook.references) {
        const cause = causesHandbook.references.find(
          r => r.id.toString() === causeId
        );
        if (cause && cause.code) {
          const causeCode = String(cause.code);
          data = ochagData[causeCode] || null;
        }
      }
    }
    
    return data || {};
  }, [ochagData, handbooks]);

  // Используем хук для работы с picker
  const {
    showPicker,
    pickerField,
    pickerOptions,
    pickerSearch,
    selectedOptionIndex,
    setSelectedOptionIndex,
    activeModalItemId,
    openPicker,
    openPickerWithOptions,
    closePicker,
    selectPickerValue: selectPickerValueBase,
    getFilteredOptions,
    getSelectedIndexInFiltered,
    setPickerSearch
  } = usePicker(
    handbooks,
    formData,
    modalData,
    updateFormData,
    updateModalData,
    loadSignsForCause,
    previousCause,
    setPreviousCause
  );

  // Обертка для selectPickerValue с обработкой выбора ВО для очага
  const selectPickerValue = useCallback(async (value) => {
    if (pickerField === 'ochag_vo' && activeModalItemId) {
      // Обработка выбора ВО для очага
      updateOchagData(activeModalItemId, { vo: value });
      closePicker();
    } else {
      // Используем стандартную логику
      await selectPickerValueBase(value);
    }
  }, [pickerField, activeModalItemId, updateOchagData, closePicker, selectPickerValueBase]);

  // Функция для открытия picker признаков (как в Form4)
  // Должна быть определена после вызова хука usePicker, чтобы использовать openPickerWithOptions
  const openSignsPicker = useCallback((itemId) => {
    const item = modalData.find(m => m.id === itemId);
    if (!item || !item.prichina) {
      alert('Сначала выберите причину');
      return;
    }
    
    // Получаем название причины из справочника
    const handbook = handbooks.find(h => h.id === 10);
    let causeName = '';
    if (handbook && handbook.references) {
      const selectedCause = handbook.references.find(r => r.id.toString() === item.prichina);
      causeName = selectedCause ? selectedCause.name : '';
    }
    
    if (!causeName) {
      alert('Не удалось определить причину');
      return;
    }
    
    // Получаем признаки для этой причины
    const signsOptions = loadedSigns[causeName] || [];
    
    if (signsOptions.length === 0) {
      alert('Признаки для выбранной причины не найдены');
      return;
    }
    
    // Используем openPickerWithOptions для открытия picker с кастомными опциями
    const currentValue = item.priznak || '';
    openPickerWithOptions('priznak', signsOptions, itemId, currentValue);
  }, [modalData, handbooks, loadedSigns, openPickerWithOptions]);

  // Функции для работы со скроллбаром таблицы
  const handleTableScroll = useCallback(() => {
    if (tableScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setTableScrollPosition(percentage);
    }
  }, []);

  const handleTableSliderChange = useCallback((e) => {
    const percentage = Number(e.target.value);
    if (tableScrollRef.current) {
      const { scrollWidth, clientWidth } = tableScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const scrollLeft = (percentage / 100) * maxScroll;
      tableScrollRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  // Функции для скроллбара таблицы расчетов
  const handleCalculationsScroll = useCallback(() => {
    if (calculationsScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = calculationsScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setCalculationsScrollPosition(percentage);
    }
  }, []);

  const handleCalculationsSliderChange = useCallback((e) => {
    const percentage = Number(e.target.value);
    if (calculationsScrollRef.current) {
      const { scrollWidth, clientWidth } = calculationsScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const scrollLeft = (percentage / 100) * maxScroll;
      calculationsScrollRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  // Функции для работы с модальным окном причин
  const openCauseModal = useCallback((rowId) => {
    setSelectedRowIdForCause(rowId);
    setShowCauseModal(true);
  }, []);

  const closeCauseModal = useCallback(() => {
    setShowCauseModal(false);
    setSelectedRowIdForCause(null);
  }, []);

  const saveCauseModal = useCallback((selectedCauseIds) => {
    if (selectedRowIdForCause !== null) {
      const currentFormData = formData || getEmptyFormData();
      // Нормализуем ID причин (приводим к числам для единообразия)
      const normalizedCauseIds = (selectedCauseIds || []).map(id => {
        return typeof id === 'string' ? parseInt(id, 10) : id;
      }).filter(id => !isNaN(id));
      
      const newData = currentFormData.data.map(item => {
        if (item.id === selectedRowIdForCause) {
          return { ...item, causes: normalizedCauseIds };
        }
        return item;
      });
      
      updateFormData({ ...currentFormData, data: newData });
    }
    closeCauseModal();
  }, [selectedRowIdForCause, formData, updateFormData, closeCauseModal]);

  // Функция для обработки клика по очагу
  const handleOchagClick = useCallback((causeKey, isChecked) => {
    // ВАЖНО: устанавливаем флаг, чтобы предотвратить перезапись данных из form6Data
    ochagDataLoadedRef.current = true;
    
    const newSelected = new Set(selectedOchags);
    
    if (isChecked) {
      // В Form5 ключ - это просто ID причины (String(cause.id)), без дефиса
      // Удаляем старые ключи с таким же ID причины (если есть)
      const causeId = causeKey.includes('-') ? causeKey.split('-')[0] : causeKey;
      Array.from(newSelected).forEach(key => {
        const keyId = key.includes('-') ? key.split('-')[0] : key;
        if (keyId === causeId) {
          newSelected.delete(key);
        }
      });
      if (newSelected.size < MAX_SELECTED_OCHAGS) {
        newSelected.add(causeKey);
        setSelectedOchags(newSelected);
        // Открываем модальное окно для редактирования
        setEditingOchagKey(causeKey);
        setIsOchagModalOpen(true);
      }
    } else {
      // Удаляем все ключи с таким же ID причины
      const causeId = causeKey.includes('-') ? causeKey.split('-')[0] : causeKey;
      Array.from(newSelected).forEach(key => {
        const keyId = key.includes('-') ? key.split('-')[0] : key;
        if (keyId === causeId) {
          newSelected.delete(key);
        }
      });
      setSelectedOchags(newSelected);
    }
  }, [selectedOchags]);

  // Синхронизация данных из Form2 - при первой инициализации или изменении количества строк
  useEffect(() => {
    
    if (independentForm2Data && dataType && independentForm2Data.poroda_characteristics_dependent) {
      
      // Проверяем длину данных Form2
      const form2Length = independentForm2Data.poroda_characteristics_dependent.length;
      
      // Получаем текущие данные Form5 из состояния (не из зависимостей)
      const currentFormData = formData || getEmptyFormData();
      
      // Синхронизируем если:
      // 1. Это первая инициализация
      // 2. Количество строк в Form2 изменилось (используем ref для отслеживания)
      const lengthChanged = form2Length !== prevForm2LengthRef.current;
      const shouldSync = !isInitializedRef.current || lengthChanged;
      
      // Обновляем ref с текущей длиной
      prevForm2LengthRef.current = form2Length;
      
      
      if (shouldSync) {
        isInitializedRef.current = true;
        
        // Создаем новые данные на основе Form2, сохраняя пользовательский ввод
        const syncedData = independentForm2Data.poroda_characteristics_dependent.map((item, index) => {
          // Ищем существующие данные в Form5 для сохранения пользовательского ввода
          // Сравниваем yarus и kodporoda (приводим к строкам для сравнения)
          const dataArray = Array.isArray(currentFormData?.data) ? currentFormData.data : [];
          const existingForm5Item = dataArray.find(existing => {
            const yarusMatch = String(existing.yarus) === String(item.yarus) || existing.yarus === item.yarus;
            const porodaMatch = String(existing.kodporoda) === String(item.kodporodato) || existing.kodporoda === item.kodporodato;
            return yarusMatch && porodaMatch;
          });
          
          // Извлекаем variant5 для получения процентов повреждения из Form2
          const variant5 = item.variant5 || {};
          
          // Извлекаем причины из variant5.prichini, если они есть
          // Приоритет: существующие данные > данные из variant5.prichini > пустой массив
          let causes = existingForm5Item?.causes;
          if (!causes || !Array.isArray(causes) || causes.length === 0) {
            if (variant5.prichini && Array.isArray(variant5.prichini)) {
              causes = variant5.prichini
                .map(prichina => {
                  if (typeof prichina === 'object' && prichina !== null && prichina.id !== undefined) {
                    return typeof prichina.id === 'string' ? parseInt(prichina.id, 10) : prichina.id;
                  } else if (typeof prichina === 'number') {
                    return prichina;
                  } else if (typeof prichina === 'string') {
                    const parsed = parseInt(prichina, 10);
                    return isNaN(parsed) ? null : parsed;
                  }
                  return null;
                })
                .map(id => id !== null && !isNaN(id) ? id : null);
            } else {
              causes = [];
            }
          }
          
          return {
            id: index + 1,
            yarus: item.yarus || '',
            // Берём долю/породу из новых полей dolap/kodporodato
            dolap: item.dolap || '',
            kodporoda: item.kodporodato || '',
            vozr: item.vozr || '',
            hsr: item.hsr || '',
            dsr: item.dsr || '',
            // Новые поля для процентов повреждения - приоритет: существующие данные > данные из variant5 > значения по умолчанию
            objedola0: existingForm5Item?.objedola0 !== undefined ? existingForm5Item.objedola0 : 100, // 0% - вычисляемое поле, изначально 100
            objedola25: existingForm5Item?.objedola25 !== undefined ? existingForm5Item.objedola25 : (variant5.cat25 || ''),
            objedola50: existingForm5Item?.objedola50 !== undefined ? existingForm5Item.objedola50 : (variant5.cat50 || ''),
            objedola75: existingForm5Item?.objedola75 !== undefined ? existingForm5Item.objedola75 : (variant5.cat75 || ''),
            objedola100: existingForm5Item?.objedola100 !== undefined ? existingForm5Item.objedola100 : (variant5.cat100 || ''),
            // Сохраняем данные модального окна
            savedModalData: existingForm5Item?.savedModalData || {},
            // Сохраняем причины (массив ID причин с типом LEAF_EATING)
            // Приоритет: существующие данные > данные из variant5.prichini > пустой массив
            causes: causes,
            // Сохраняем состояние галочки
            causeChecked: existingForm5Item?.causeChecked || false
            // dolapovr - вычисляемое поле (сумма столбцов 1-25%, 26-50%, 51-75%, 76-100%)
          };
        });
        
        const newFormData = {
          ...currentFormData,
          data: syncedData,
          itaks5: currentFormData?.itaks5 || '',
          savedModalData: currentFormData?.savedModalData || {}
        };
        updateFormData(newFormData);
      }
    }
  }, [independentForm2Data, dataType, updateFormData, isActive]);

  // Сброс флага инициализации при деактивации формы
  useEffect(() => {
    if (!isActive) {
      isInitializedRef.current = false;
      prevForm2LengthRef.current = 0;
    }
  }, [isActive]);

  // Инициализация скроллбара
  useEffect(() => {
    const handleTableScroll = () => {
      if (tableScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current;
        const maxScroll = scrollWidth - clientWidth;
        const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
        setTableScrollPosition(percentage);
      }
    };

    // Устанавливаем обработчики событий
    const tableElement = tableScrollRef.current;

    if (tableElement) {
      tableElement.addEventListener('scroll', handleTableScroll);
    }

    // Инициализируем позиции слайдеров
    handleTableScroll();

    // Очистка обработчиков при размонтировании
    return () => {
      if (tableElement) {
        tableElement.removeEventListener('scroll', handleTableScroll);
      }
    };
  }, []);

  // Инициализация скроллбара таблицы расчетов
  useEffect(() => {
    const handleCalculationsScroll = () => {
      if (calculationsScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = calculationsScrollRef.current;
        const maxScroll = scrollWidth - clientWidth;
        const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
        setCalculationsScrollPosition(percentage);
      }
    };

    // Устанавливаем обработчики событий
    const calculationsElement = calculationsScrollRef.current;

    if (calculationsElement) {
      calculationsElement.addEventListener('scroll', handleCalculationsScroll);
    }

    // Инициализируем позиции слайдеров
    handleCalculationsScroll();

    // Очистка обработчиков при размонтировании
    return () => {
      if (calculationsElement) {
        calculationsElement.removeEventListener('scroll', handleCalculationsScroll);
      }
    };
  }, []);

  // Валидация обязательных полей Form5 - все поля необязательные
  useEffect(() => {
    // Все поля необязательные, поэтому ошибок валидации нет
    const hasErrors = false;
    
    if (hasValidationErrors !== hasErrors) {
      setHasValidationErrors(hasErrors);
    }
  }, [hasValidationErrors, setHasValidationErrors]);

  // Эффекты для picker'ов (как в Form4)
  useEffect(() => {
    if (showPicker) {
      const timer = setTimeout(() => {
        const filteredIndex = getSelectedIndexInFiltered();
        if (filteredIndex >= 0) {
          const selectedElement = document.querySelector(`.picker-list li:nth-child(${filteredIndex + 1})`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showPicker, selectedOptionIndex, getFilteredOptions, getSelectedIndexInFiltered, pickerOptions, pickerSearch]);

  // Обработчик клавиатуры для picker'а (как в Form4)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showPicker) return;
      
      const filteredOptions = getFilteredOptions();
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedOptionIndex(prev => {
          const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
          return newIndex;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedOptionIndex(prev => prev > 0 ? prev - 1 : prev);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const currentFilteredIndex = getSelectedIndexInFiltered();
        if (currentFilteredIndex >= 0 && currentFilteredIndex < filteredOptions.length) {
          selectPickerValue(filteredOptions[currentFilteredIndex].value);
        }
      } else if (e.key === 'Escape') {
        closePicker();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPicker, selectedOptionIndex, getFilteredOptions, getSelectedIndexInFiltered, selectPickerValue, closePicker]);

  // Сброс selectedOptionIndex при изменении поиска (как в Form4)
  useEffect(() => {
    if (pickerSearch) {
      setSelectedOptionIndex(-1);
    }
  }, [pickerSearch]);

  // Ref для отслеживания загрузки данных очагов из form6Data
  const ochagDataLoadedRef = useRef(false);
  const prevForm6DataRef = useRef(null);

  // Загрузка данных очагов из form6Data при инициализации (точно как в Form4)
  useEffect(() => {
    const form6Data = formData?.form6;
    
    // Загружаем очаги только если form6Data изменился И нет локальных данных из formData
    if (!form6Data || !handbooks) {
      return;
    }

    // Если уже загрузили данные из formData, проверяем, есть ли там данные о ВО
    // Если нет, загружаем из form6Data
    if (ochagDataLoadedRef.current) {
      // Проверяем, есть ли в formData.ochagData данные о ВО для очагов
      const hasVO = formData?.ochagData && Object.values(formData.ochagData).some(
        ochag => ochag.vo
      );
      if (hasVO) {
        return; // Данные уже есть, не перезаписываем
      }
    }

    // Проверяем, изменились ли данные form6
    const form6DataKey = JSON.stringify({
      selectedOchagCodes: form6Data.selectedOchagCodes || [],
      vidvo: form6Data.vidvo || {}
    });
    
    if (prevForm6DataRef.current === form6DataKey) {
      return; // Данные не изменились, пропускаем
    }
    
    prevForm6DataRef.current = form6DataKey;

    let selectedOchagCodes = form6Data.selectedOchagCodes || [];
    const vidvo = form6Data.vidvo || {};

    // Если selectedOchagCodes пустой, но есть vidvo, извлекаем коды из ключей vidvo
    if (selectedOchagCodes.length === 0 && vidvo && Object.keys(vidvo).length > 0) {
      selectedOchagCodes = Object.keys(vidvo).map(key => {
        const code = parseInt(key, 10);
        return isNaN(code) ? null : code;
      }).filter(code => code !== null);
    }
    
    if (selectedOchagCodes.length === 0 && Object.keys(vidvo).length === 0) {
      return;
    }

    // Получаем справочник причин (ID 10) для поиска по кодам
    const causesHandbook = handbooks.find(h => h.id === 10);
    if (!causesHandbook || !causesHandbook.references) {
      return;
    }

    // Находим причины по их кодам и отмечаем их галочками
    const newSelectedOchags = new Set();
    const newOchagData = {};

    selectedOchagCodes.forEach((ochagCode) => {
      // Ищем причину по коду в справочнике
      const cause = causesHandbook.references.find(
        r => r.code && r.code.toString() === String(ochagCode)
      );

      if (!cause) {
        return;
      }
      
      // В Form5 используем ID причины как ключ (без индекса, так как очаги не разделяются по ярусам)
      const causeKey = String(cause.id);
      const causeCode = String(ochagCode); // Код причины из form6Data
      
      // Отмечаем причину галочкой
      newSelectedOchags.add(causeKey);

      // Загружаем данные очага из vidvo, если они есть
      if (vidvo[causeCode]) {
        const ochagInfo = vidvo[causeCode];
        
        // Получаем ID ВО из vidvo
        let voId = '';
        if (ochagInfo.vidvo && ochagInfo.vidvo.id) {
          voId = String(ochagInfo.vidvo.id);
        }

        // Определяем, является ли очаг главным (если есть ochagPhasa или ochagType)
        const isMain = !!(ochagInfo.ochagPhasa || ochagInfo.ochagType);
        
        // Преобразуем фазу из формата сервера (ACTIVE, INITIAL, etc.) в формат для модального окна
        let phase = '';
        if (ochagInfo.ochagPhasa) {
          // Сохраняем фазу в формате сервера (INITIAL, POPULATION_GROWTH, OUTBREAK, CRISIS, EMERGING, ACTIVE, FADING)
          // Модальное окно использует тот же формат
          phase = ochagInfo.ochagPhasa;
        }

        // Преобразуем тип из формата сервера (EPISODIC, CHRONIC) в формат для модального окна
        let type = '';
        if (ochagInfo.ochagType) {
          // Модальное окно использует формат сервера (EPISODIC, CHRONIC)
          type = ochagInfo.ochagType;
        }

        const ochagDataValue = {
          isMain: isMain,
          vo: voId,
          phase: phase,
          type: type
        };

        // Сохраняем данные очага и по ключу с ID, и по коду причины (как в Form6)
        newOchagData[causeKey] = ochagDataValue;
        newOchagData[causeCode] = ochagDataValue;
      }
    });

    // Обновляем состояние только если есть изменения И нет локальных данных (точно как в Form4)
    if ((newSelectedOchags.size > 0 || Object.keys(newOchagData).length > 0) && !ochagDataLoadedRef.current) {
      setSelectedOchags(prev => {
        // Если локальных данных нет, используем данные из form6Data
        if (prev.size === 0) {
          return newSelectedOchags;
        }
        // Иначе объединяем с существующими
        const combined = new Set(prev);
        newSelectedOchags.forEach(key => combined.add(key));
        return combined;
      });
      setOchagData(prev => {
        // Если локальных данных нет, используем данные из form6Data
        if (Object.keys(prev).length === 0) {
          return newOchagData;
        }
        // Иначе объединяем с существующими
        const merged = { ...prev, ...newOchagData };
        return merged;
      });
      
      // Устанавливаем флаг после успешной загрузки
      ochagDataLoadedRef.current = true;
    }
  }, [formData?.form6, handbooks]);


  // Сохранение данных очагов в formData при их изменении
  const prevSelectedOchagsRef = useRef(null);
  const prevOchagDataRef = useRef(null);
  
  useEffect(() => {
    // Проверяем, изменились ли данные
    const selectedOchagsStr = JSON.stringify(Array.from(selectedOchags).sort());
    const ochagDataStr = JSON.stringify(ochagData);
    
    if (prevSelectedOchagsRef.current === selectedOchagsStr && prevOchagDataRef.current === ochagDataStr) {
      return; // Данные не изменились
    }
    
    prevSelectedOchagsRef.current = selectedOchagsStr;
    prevOchagDataRef.current = ochagDataStr;

    const selectedOchagsArray = Array.from(selectedOchags);

    // Сохраняем данные в formData (даже при деактивации формы)
    // ВАЖНО: сохраняем selectedOchags всегда, даже если пустой массив
    setFormData(prev => {
      const currentFormData = prev || getEmptyFormData();
      
      // Преобразуем данные очагов в формат form6.vidvo для синхронизации с формой №6
      const vidvo = { ...(currentFormData.form6?.vidvo || {}) };
      const causesHandbook = handbooks.find(h => h.id === 10);
      
      // Проходим по всем данным очагов и обновляем vidvo по коду причины
      Object.keys(ochagData).forEach(key => {
        // В Form5 ключи могут быть как ID причины (String(cause.id)), так и код причины
        // Работаем с обоими форматами
        const ochagInfo = ochagData[key];
        if (!ochagInfo) {
          return;
        }
        
        // Определяем код причины
        let causeCode = null;
        
        // Если ключ - это код причины (число или строка-число), используем его
        if (!key.includes('-') && (typeof key === 'string' || typeof key === 'number')) {
          // Проверяем, является ли ключ кодом причины
          const causesHandbook = handbooks.find(h => h.id === 10);
          if (causesHandbook && causesHandbook.references) {
            const causeByCode = causesHandbook.references.find(
              r => r.code && String(r.code) === String(key)
            );
            if (causeByCode) {
              causeCode = String(key);
            } else {
              // Если не нашли по коду, возможно это ID причины
              const causeById = causesHandbook.references.find(
                r => String(r.id) === String(key)
              );
              if (causeById && causeById.code) {
                causeCode = String(causeById.code);
              }
            }
          }
        }
        
        if (causeCode && causesHandbook && causesHandbook.references) {
          // Преобразуем ВО в формат Form6
          let vidvoObj = null;
          if (ochagInfo.vo) {
            const voHandbook = handbooks.find(h => h.id === 40);
            if (voHandbook && voHandbook.references) {
              const vo = voHandbook.references.find(
                r => r.id.toString() === String(ochagInfo.vo)
              );
              if (vo) {
                vidvoObj = { id: vo.id };
              }
            }
          }
          
          // Преобразуем фазу и тип из формата формы в формат сервера
          let ochagPhasa = null;
          if (ochagInfo.phase) {
            // Сохраняем фазу в формате сервера (INITIAL, POPULATION_GROWTH, OUTBREAK, CRISIS, EMERGING, ACTIVE, FADING)
            ochagPhasa = ochagInfo.phase;
          }
          
          let ochagType = null;
          if (ochagInfo.type) {
            // Сохраняем тип в формате сервера (EPISODIC, CHRONIC)
            ochagType = ochagInfo.type;
          }
          
          // Обновляем или создаем запись в vidvo
          vidvo[causeCode] = {
            ...(vidvo[causeCode] || {}),
            vidvo: vidvoObj,
            ochagPhasa: ochagPhasa,
            ochagType: ochagType
          };
        }
      });
      
      // Удаляем данные очагов, которые больше не выбраны
      const selectedCauseCodes = new Set();
      Array.from(selectedOchags).forEach(key => {
        // В Form5 ключ - это ID причины (String(cause.id))
        const causeId = String(key);
        if (causesHandbook && causesHandbook.references) {
          const cause = causesHandbook.references.find(
            r => r.id.toString() === causeId
          );
          if (cause && cause.code) {
            selectedCauseCodes.add(String(cause.code));
          }
        }
      });
      
      // Удаляем из vidvo очаги, которые больше не выбраны
      Object.keys(vidvo).forEach(code => {
        if (!selectedCauseCodes.has(code)) {
          delete vidvo[code];
        }
      });
      
      return {
        ...prev,
        form5: {
          ...(prev?.form5 || {}),
          ...currentFormData
        },
        // ВАЖНО: сохраняем selectedOchags на верхнем уровне formData
        // Это нужно, чтобы форма №6 могла их читать, и они не перезаписывались
        selectedOchags: selectedOchagsArray,
        ochagData: ochagData,
        form6: {
          ...(currentFormData.form6 || {}),
          vidvo: vidvo
        }
      };
    });
  }, [selectedOchags, ochagData, formData, setFormData, handbooks]);

  // Функция для получения значения доли поврежденных деревьев для конкретной причины по ID (для карточек очагов)
  const getOchagDamageValueForCause = useCallback(() =>
    calculateTotalsByNasazhdeniya().dolapovr,
    [calculateTotalsByNasazhdeniya]
  );

  return (
    <>
      <div className="template-section">
        <div 
          className="template-header" 
          style={{ cursor: 'pointer', padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h3>V. Форма ввода ВНН за лесопатологическим состоянием насаждений глазомерным методом</h3>
        </div>
        
        <div className="template-content">
          <DataSection
            displayData={displayData}
            savedModalData={savedModalData}
            calculateDamage_0_func={calculateDamage_0_func}
            calculateDamagePercentage_func={calculateDamagePercentage_func}
            isDamageSumValid_func={isDamageSumValid_func}
            updateDataItem={updateDataItem}
            openModal={openModal}
            openCauseModal={openCauseModal}
            tableScrollRef={tableScrollRef}
            tableScrollPosition={tableScrollPosition}
            handleTableSliderChange={handleTableSliderChange}
            handleTableScroll={handleTableScroll}
            handbooks={handbooks}
          />
          
          <OchagCardsSection
            form5Data={displayData}
            handbooks={handbooks}
            independentForm2Data={independentForm2Data}
            form1Data={form1Data}
            dataType={dataType}
            calculateTotalsByNasazhdeniya={calculateTotalsByNasazhdeniya}
            calculateDamagePercentage_func={calculateDamagePercentage_func}
            formData={formData}
            onOchagClick={handleOchagClick}
            selectedOchags={selectedOchags}
            isDisabled={selectedOchags.size >= MAX_SELECTED_OCHAGS}
            getOchagDamageValueForCause={getOchagDamageValueForCause}
          />
          
          <CalculationsSection
            displayData={displayData}
            calculateDamage_0_func={calculateDamage_0_func}
            calculateDamagePercentage_func={calculateDamagePercentage_func}
            calculateTotalDamage_0={calculateTotalDamage_0}
            calculateTotalDamage_1_25={calculateTotalDamage_1_25}
            calculateTotalDamage_25_1_50={calculateTotalDamage_25_1_50}
            calculateTotalDamage_50_1_75={calculateTotalDamage_50_1_75}
            calculateTotalDamage_75_1_100={calculateTotalDamage_75_1_100}
            calculateTotalDamagePercentage={calculateTotalDamagePercentage}
            calculationsScrollRef={calculationsScrollRef}
            calculationsScrollPosition={calculationsScrollPosition}
            handleCalculationsSliderChange={handleCalculationsSliderChange}
            handleCalculationsScroll={handleCalculationsScroll}
          />

          <InfoSection
            displayData={displayData}
            handleUserInfoChange={handleUserInfoChange}
          />
        </div>

        <CauseSignModal
          isModalOpen={isModalOpen}
          selectedRowId={selectedRowId}
          modalData={modalData}
          handbooks={handbooks}
          loadedSigns={loadedSigns}
          isLoadingSigns={isLoadingSigns}
          openPicker={openPicker}
          openYearPicker={openYearPicker}
          openSignsPicker={openSignsPicker}
          addModalRow={addModalRow}
          removeModalRow={removeModalRow}
          closeModal={closeModal}
          saveModalData={saveModalData}
          updateModalData={updateModalData}
        />

        <PickerModal
          showPicker={showPicker}
          pickerSearch={pickerSearch}
          setPickerSearch={setPickerSearch}
          getFilteredOptions={getFilteredOptions}
          getSelectedIndexInFiltered={getSelectedIndexInFiltered}
          pickerValue={(() => {
            if (pickerField === 'ochag_vo' && activeModalItemId) {
              // Для ВО очага берем значение из ochagData
              return getOchagDataByKey(activeModalItemId)?.vo || '';
            } else if (activeModalItemId) {
              const item = modalData.find(m => m.id === activeModalItemId);
              return item ? item[pickerField] || '' : '';
            } else {
              // Для полей формы берем значение из formData
              return formData ? (formData[pickerField] || '') : '';
            }
          })()}
          selectPickerValue={selectPickerValue}
          closePicker={closePicker}
        />

        <YearPickerModal
          showYearPicker={showYearPicker}
          selectYear={(year) => {
            updateModalData(activeYearItemId, 'godpovr', year.toString());
            closeYearPicker();
          }}
          closeYearPicker={closeYearPicker}
          activeYearItemId={activeYearItemId}
          modalData={modalData}
          updateModalData={updateModalData}
        />

        <CauseModal
          showModal={showCauseModal}
          onClose={closeCauseModal}
          onSave={saveCauseModal}
          handbooks={handbooks}
          selectedCauses={(() => {
            if (selectedRowIdForCause !== null) {
              const item = displayData.data?.find(d => d.id === selectedRowIdForCause);
              return item?.causes || [];
            }
            return [];
          })()}
        />

        {/* Модальное окно для редактирования очага */}
        {isOchagModalOpen && editingOchagKey && (() => {
          // Получаем тип причины для определения доступных фаз
          const getAvailablePhases = () => {
            const causeId = editingOchagKey.split('-')[0];
            const causesHandbook = handbooks.find(h => h.id === 10);
            let causeType = null;
            
            if (causesHandbook && causesHandbook.references) {
              const cause = causesHandbook.references.find(
                r => r.id.toString() === causeId
              );
              if (cause) {
                causeType = cause.type;
              }
            }
            
            // Все возможные фазы
            const allPhases = [
              { value: 'INITIAL', label: 'Начальная' },
              { value: 'POPULATION_GROWTH', label: 'Нарастания численности' },
              { value: 'OUTBREAK', label: 'Собственно вспышка' },
              { value: 'CRISIS', label: 'Кризис' },
              { value: 'EMERGING', label: 'Возникающий' },
              { value: 'ACTIVE', label: 'Действующий' },
              { value: 'FADING', label: 'Затухающий' }
            ];
            
            // Если тип STEM_PESTS или LEAF_EATING, показываем только первые 4 фазы
            let phases = [];
            if (causeType === 'STEM_PESTS' || causeType === 'LEAF_EATING') {
              phases = allPhases.slice(0, 4);
            } else {
              // Для остальных типов показываем все фазы
              phases = allPhases;
            }
            
            // Проверяем, есть ли уже выбранная фаза, которая не входит в список доступных
            // Если есть, добавляем её в список, чтобы не потерять данные
            const currentPhase = getOchagDataByKey(editingOchagKey)?.phase;
            if (currentPhase && !phases.find(p => p.value === currentPhase)) {
              const phaseToAdd = allPhases.find(p => p.value === currentPhase);
              if (phaseToAdd) {
                phases = [...phases, phaseToAdd];
              }
            }
            
            return phases;
          };
          
          const availablePhases = getAvailablePhases();
          
          return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={() => {
              setIsOchagModalOpen(false);
              setEditingOchagKey(null);
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '900px',
                width: '95%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '20px',
                borderBottom: '1px solid #eee',
                paddingBottom: '10px'
              }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                  Редактирование очага
                </h3>
                <button
                  onClick={() => {
                    setIsOchagModalOpen(false);
                    setEditingOchagKey(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div 
                  style={{ 
                    overflowX: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <table className="table" style={{ marginBottom: 0, minWidth: '800px' }}>
                    <thead>
                      <tr>
                        <th style={{ minWidth: '150px', backgroundColor: '#f8f9fa' }}>Главный очаг</th>
                        <th style={{ minWidth: '200px', backgroundColor: '#f8f9fa' }}>{getOchagDataByKey(editingOchagKey)?.isMain ? 'ВО' : 'Вид ВО'}</th>
                        {getOchagDataByKey(editingOchagKey)?.isMain && (
                          <>
                            <th style={{ minWidth: '150px', backgroundColor: '#f8f9fa' }}>Фаза</th>
                            <th style={{ minWidth: '150px', backgroundColor: '#f8f9fa' }}>Тип</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ 
                          padding: '0',
                          border: '1px solid #dee2e6',
                          textAlign: 'center'
                        }}>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            margin: 0,
                            minHeight: '40px',
                            padding: '8px'
                          }}>
                            <input
                              type="checkbox"
                              checked={getOchagDataByKey(editingOchagKey)?.isMain || false}
                              onChange={(e) => {
                                const isMain = e.target.checked;
                                const currentData = getOchagDataByKey(editingOchagKey);
                                
                                // Если делаем главным, убираем главный статус у других очагов
                                if (isMain) {
                                  const newOchagData = { ...ochagData };
                                  Object.keys(newOchagData).forEach(key => {
                                    if (key !== editingOchagKey) {
                                      const keyCauseId = key.includes('-') ? key.split('-')[0] : null;
                                      const editingCauseId = editingOchagKey.split('-')[0];
                                      
                                      if (keyCauseId && keyCauseId !== editingCauseId && newOchagData[key]?.isMain) {
                                        newOchagData[key] = { ...newOchagData[key], isMain: false, phase: '', type: '' };
                                      }
                                      if (!key.includes('-')) {
                                        const causesHandbook = handbooks.find(h => h.id === 10);
                                        if (causesHandbook && causesHandbook.references) {
                                          const cause = causesHandbook.references.find(
                                            r => r.code && String(r.code) === key
                                          );
                                          if (cause && String(cause.id) !== editingCauseId && newOchagData[key]?.isMain) {
                                            newOchagData[key] = { ...newOchagData[key], isMain: false, phase: '', type: '' };
                                          }
                                        }
                                      }
                                    }
                                  });
                                  
                                  newOchagData[editingOchagKey] = { ...currentData, isMain: true };
                                  
                                  const causeId = editingOchagKey.split('-')[0];
                                  const causesHandbook = handbooks.find(h => h.id === 10);
                                  if (causesHandbook && causesHandbook.references) {
                                    const cause = causesHandbook.references.find(
                                      r => r.id.toString() === causeId
                                    );
                                    if (cause && cause.code) {
                                      const causeCode = String(cause.code);
                                      newOchagData[causeCode] = { ...currentData, isMain: true };
                                    }
                                  }
                                  
                                  setOchagData(newOchagData);
                                } else {
                                  updateOchagData(editingOchagKey, {
                                    isMain: false,
                                    phase: '',
                                    type: ''
                                  });
                                }
                              }}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          </label>
                        </td>
                        <td style={{ 
                          padding: '0',
                          border: '1px solid #dee2e6',
                          textAlign: 'center'
                        }}>
                          <div
                            className="reference-cell"
                            onClick={() => {
                              const voHandbook = handbooks.find(h => h.id === 40);
                              const causesHandbook = handbooks.find(h => h.id === 10);
                              const causeId = editingOchagKey.split('-')[0];
                              const selectedCause = causesHandbook?.references?.find(
                                r => r.id.toString() === String(causeId)
                              );
                              const allowedDiseaseCodes = new Set(
                                Array.isArray(selectedCause?.disease_codes)
                                  ? selectedCause.disease_codes.map(code => String(code))
                                  : []
                              );

                              if (voHandbook && voHandbook.references) {
                                const options = voHandbook.references
                                  .filter(r => {
                                    if (allowedDiseaseCodes.size === 0) {
                                      return false;
                                    }
                                    return allowedDiseaseCodes.has(String(r.code));
                                  })
                                  .map(r => ({
                                    value: r.id.toString(),
                                    label: r.name
                                  }));
                                openPickerWithOptions('ochag_vo', options, editingOchagKey, getOchagDataByKey(editingOchagKey)?.vo || '');
                              }
                            }}
                            style={{ 
                              width: '100%',
                              height: '100%',
                              minHeight: '40px',
                              cursor: 'pointer',
                              background: 'transparent',
                              border: 'none',
                              padding: '0',
                              margin: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <span style={{ 
                              color: getOchagDataByKey(editingOchagKey)?.vo ? '#000' : '#6c757d',
                              fontSize: '14px',
                              flex: 1,
                              padding: '8px'
                            }}>
                              {(() => {
                                const currentOchagData = getOchagDataByKey(editingOchagKey);
                                if (!currentOchagData?.vo) return 'Выберите ВО';
                                const voHandbook = handbooks.find(h => h.id === 40);
                                if (voHandbook && voHandbook.references) {
                                  const vo = voHandbook.references.find(
                                    r => r.id.toString() === String(currentOchagData.vo)
                                  );
                                  return vo ? vo.name : currentOchagData.vo;
                                }
                                return currentOchagData.vo;
                              })()}
                            </span>
                            <span style={{ color: '#6c757d', fontSize: '12px', paddingRight: '8px' }}>☰</span>
                          </div>
                        </td>
                        {getOchagDataByKey(editingOchagKey)?.isMain && (
                          <>
                            <td style={{ 
                              padding: '0',
                              border: '1px solid #dee2e6',
                              textAlign: 'center'
                            }}>
                              <select
                                value={getOchagDataByKey(editingOchagKey)?.phase || ''}
                                onChange={(e) => {
                                  const currentData = getOchagDataByKey(editingOchagKey);
                                  const newPhase = e.target.value;
                                  // Если указана фаза или тип, автоматически делаем очаг главным
                                  const isMain = !!(newPhase || currentData?.type);
                                  updateOchagData(editingOchagKey, { 
                                    phase: newPhase || '', 
                                    isMain: isMain 
                                  });
                                }}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  minHeight: '40px',
                                  padding: '8px 12px',
                                  border: 'none',
                                  borderRadius: '0',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  backgroundColor: 'transparent',
                                  color: '#212529',
                                  outline: 'none'
                                }}
                              >
                                <option value="">Выберите фазу</option>
                                {availablePhases.map(phase => (
                                  <option key={phase.value} value={phase.value}>
                                    {phase.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={{ 
                              padding: '0',
                              border: '1px solid #dee2e6',
                              textAlign: 'center'
                            }}>
                              <select
                                value={getOchagDataByKey(editingOchagKey)?.type || ''}
                                onChange={(e) => {
                                  const currentData = getOchagDataByKey(editingOchagKey);
                                  const newType = e.target.value;
                                  // Если указана фаза или тип, автоматически делаем очаг главным
                                  const isMain = !!(currentData?.phase || newType);
                                  updateOchagData(editingOchagKey, { 
                                    type: newType || '', 
                                    isMain: isMain 
                                  });
                                }}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  minHeight: '40px',
                                  padding: '8px 12px',
                                  border: 'none',
                                  borderRadius: '0',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  backgroundColor: 'transparent',
                                  color: '#212529',
                                  outline: 'none'
                                }}
                              >
                                <option value="">Выберите тип</option>
                                <option value="EPISODIC">Эпизодический</option>
                                <option value="CHRONIC">Хронический</option>
                              </select>
                            </td>
                          </>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {(() => {
                const closeModal = () => {
                  setIsOchagModalOpen(false);
                  setEditingOchagKey(null);
                };
                const cur = getOchagDataByKey(editingOchagKey) || {};
                const isMain = !!cur.isMain;
                const voOk = cur.vo != null && String(cur.vo).trim() !== '';
                const phaseOk = !isMain || (cur.phase != null && String(cur.phase).trim() !== '');
                const typeOk = !isMain || (cur.type != null && String(cur.type).trim() !== '');
                const isComplete = voOk && phaseOk && typeOk;
                return (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px'
                  }}>
                    <button
                      onClick={closeModal}
                      style={{
                        minWidth: '100px',
                        padding: '8px 16px',
                        backgroundColor: '#fff',
                        color: '#495057',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Закрыть
                    </button>
                    <button
                      onClick={closeModal}
                      disabled={!isComplete}
                      title={isComplete ? '' : 'Заполните все поля очага'}
                      style={{
                        minWidth: '100px',
                        padding: '8px 16px',
                        backgroundColor: isComplete ? '#495058' : '#fff',
                        color: isComplete ? '#fff' : '#adb5bd',
                        border: `1px solid ${isComplete ? '#495058' : '#ced4da'}`,
                        borderRadius: '4px',
                        cursor: isComplete ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Готово
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
          );
        })()}

      </div>
    </>
  );
};

export default Form5;
